<template>
  <v-dialog
    v-model="active"
    max-width="580"
    :persistent="busy"
    scrollable>
    <div class="bcsv bcsv--bare">
      <div
        class="bcsv-card"
        style="border-radius: 14px;">
        <div class="bcsv-modal-head">
          <div>
            <h2>Upload CSV</h2>
            <div
              class="bcsv-sub"
              style="margin-top: 4px; font-size: 13px;">
              สร้าง Withdraw Note แบบ batch จากไฟล์เดียว
            </div>
          </div>
          <button
            type="button"
            class="bcsv-x"
            :disabled="busy"
            @click="close">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6">
              <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />
            </svg>
          </button>
        </div>

        <div
          class="bcsv-modal-body"
          style="max-height: calc(100vh - 260px);">
          <div class="bcsv-field">
            <label class="bcsv-label">From warehouse (ต้นทาง)</label>
            <select
              v-model="fromWarehouseId"
              class="bcsv-input"
              :disabled="busy"
              @change="errorMessage = ''">
              <option :value="null">
                เลือกคลังต้นทาง…
              </option>
              <option
                v-for="warehouse in warehouses"
                :key="warehouse.id"
                :value="warehouse.id">
                {{ warehouse.name }}
              </option>
            </select>
            <div class="bcsv-hint">
              ทุกแถวในไฟล์จะใช้คลังต้นทางเดียวกันนี้
            </div>
          </div>

          <div class="bcsv-field">
            <label class="bcsv-label">
              Due date
              <span class="bcsv-badge">Optional</span>
            </label>
            <input
              v-model="dueDate"
              type="date"
              class="bcsv-input"
              :min="today"
              :class="{ 'bcsv-input--error': dueDateError }"
              :disabled="busy"
              @input="dueDateError = ''">
            <div
              v-if="dueDateError"
              class="bcsv-field-error">
              {{ dueDateError }}
            </div>
            <div class="bcsv-hint">
              วันที่ต้องการให้ Withdraw Note ทุกใบในไฟล์นี้เสร็จ — เว้นว่างได้
            </div>
          </div>

          <div
            class="bcsv-drop"
            :class="{ 'bcsv-drop--has-file': !!file, 'bcsv-drop--over': dragOver }"
            @click="pickFile"
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="dragOver = false"
            @drop.prevent="onDrop">
            <div v-if="!file">
              <svg
                width="24"
                height="24"
                viewBox="0 0 14 14"
                fill="none"
                stroke="#9AA3AE"
                stroke-width="1.3"
                style="margin-bottom: 10px;">
                <path d="M7 10.5V2.5M4 5.5 7 2.5l3 3" />
                <path d="M1.5 10.5V12h11v-1.5" />
              </svg>
              <div style="font-size: 14px;">
                ลากไฟล์มาวาง หรือ <span class="bcsv-drop-pick">เลือกไฟล์จากเครื่อง</span>
              </div>
              <div class="bcsv-hint">
                รองรับ .csv ขนาดไม่เกิน 5 MB และไม่เกิน {{ maxRowsText }} แถว
              </div>
            </div>

            <div
              v-else
              class="bcsv-file-row">
              <span class="bcsv-file-tag">CSV</span>
              <div style="flex: 1; min-width: 0;">
                <div
                  class="bcsv-mono bcsv-file-name"
                  style="font-size: 13px;">
                  {{ file.name }}
                </div>
                <div class="bcsv-hint">
                  {{ fileMeta }}
                </div>
              </div>
              <button
                type="button"
                class="bcsv-btn bcsv-btn--sm"
                :disabled="busy"
                @click.stop="pickFile">
                เปลี่ยนไฟล์
              </button>
            </div>
          </div>

          <input
            ref="fileInput"
            type="file"
            accept=".csv,text/csv"
            style="display: none;"
            @change="onFileInput">

          <div class="bcsv-format">
            <div class="bcsv-format-title">
              รูปแบบไฟล์ที่ต้องการ
            </div>
            <div
              class="d-flex flex-wrap"
              style="gap: 8px; margin-bottom: 12px;">
              <span
                v-for="col in columns"
                :key="col"
                class="bcsv-col-chip bcsv-mono">
                {{ col }}
              </span>
            </div>
            <ul class="bcsv-rules">
              <li
                v-for="rule in ruleItems"
                :key="rule">
                {{ rule }}
              </li>
            </ul>
            <div
              class="d-flex align-center flex-wrap"
              style="gap: 14px; margin-top: 14px;">
              <a
                class="bcsv-link"
                href="/files/batch-transfer-template.csv"
                download>
                ดาวน์โหลด template.csv
              </a>
              <batch-transfer-copy-prompt-button
                :prompt="generatePrompt"
                label="Copy prompt สำหรับ gen ไฟล์" />
            </div>
            <div class="bcsv-hint">
              คัดลอก prompt ไปให้ AI ช่วยสร้างไฟล์ CSV ตามฟอร์แมตนี้
            </div>
          </div>

          <div
            v-if="errorMessage"
            class="bcsv-alert">
            {{ errorMessage }}
          </div>
        </div>

        <div class="bcsv-modal-foot">
          <div style="font-size: 12px; color: #9AA3AE;">
            {{ busy
              ? 'กำลังตรวจสอบไฟล์และสร้าง Withdraw Note — อย่าปิดหน้าต่างนี้'
              : 'ระบบจะตรวจสอบไฟล์และสร้าง Withdraw Note ทันที อาจใช้เวลาสักครู่' }}
          </div>
          <div
            class="d-flex"
            style="gap: 10px;">
            <button
              type="button"
              class="bcsv-btn"
              :disabled="busy"
              @click="close">
              ยกเลิก
            </button>
            <button
              type="button"
              class="bcsv-btn bcsv-btn--primary"
              :disabled="!canSubmit"
              @click="submit">
              {{ busy ? 'กำลังส่ง…' : 'อัปโหลดและประมวลผล' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import BatchTransferProvider from '@/resources/BatchTransferProvider'
import BatchTransferCopyPromptButton from './BatchTransferCopyPromptButton.vue'
import { buildGeneratePrompt, RULE_ITEMS } from '@/assets/js/BatchTransferPrompts'

const batchTransferProvider = new BatchTransferProvider()
const MAX_FILE_SIZE = 5 * 1024 * 1024
const MAX_ROWS = 2000

export default {
  name: 'BatchTransferModal',
  components: {
    BatchTransferCopyPromptButton
  },
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      active: this.value,
      fromWarehouseId: null,
      dueDate: '',
      dueDateError: '',
      file: null,
      rowCount: null,
      dragOver: false,
      busy: false,
      errorMessage: '',
      columns: ['sku', 'toWarehouse', 'qty', 'group']
    }
  },
  computed: {
    ruleItems () {
      return RULE_ITEMS
    },
    ...mapGetters({
      mapWarehouse: 'Store/mapWarehouse'
    }),
    warehouses () {
      return this.mapWarehouse.filter((warehouse) => warehouse.id !== 0)
    },
    today () {
      return this.$dayjs().format('YYYY-MM-DD')
    },
    maxRowsText () {
      return MAX_ROWS.toLocaleString('en-US')
    },
    canSubmit () {
      return !this.busy && !!this.file && !!this.fromWarehouseId
    },
    fileMeta () {
      if (!this.file) return ''
      const size = `${Math.max(1, Math.round(this.file.size / 1024))} KB`
      const rows = this.rowCount === null ? '' : `${this.rowCount.toLocaleString('en-US')} แถว · `
      const modified = this.file.lastModified
        ? ` · แก้ไขล่าสุด ${this.$dayjs(this.file.lastModified).format('D MMM YYYY')}`
        : ''
      return `${rows}${size}${modified}`
    },
    generatePrompt () {
      const warehouse = this.warehouses.find((w) => w.id === this.fromWarehouseId)
      return buildGeneratePrompt({ fromWarehouseName: warehouse ? warehouse.name : '' })
    }
  },
  watch: {
    value (val) {
      this.active = val
      if (val) this.resetForm()
    },
    active (val) {
      this.$emit('input', val)
    }
  },
  mounted () {
    window.addEventListener('beforeunload', this.guardUnload)
  },
  beforeDestroy () {
    window.removeEventListener('beforeunload', this.guardUnload)
  },
  methods: {
    // งานฝั่ง server เดินต่อแม้ปิดแท็บ — เตือนไว้กันผู้ใช้อัปซ้ำแล้วได้ Withdraw Note สองชุด
    guardUnload (event) {
      if (!this.busy) return undefined
      event.preventDefault()
      // ต้องเซ็ต returnValue ด้วย เบราว์เซอร์เก่าถึงจะขึ้น dialog ยืนยัน
      Object.assign(event, { returnValue: '' })
      return ''
    },
    pickFile () {
      if (this.busy) return
      this.$refs.fileInput.value = ''
      this.$refs.fileInput.click()
    },
    onFileInput (event) {
      this.setFile(event.target.files && event.target.files[0])
    },
    onDrop (event) {
      this.dragOver = false
      if (this.busy) return
      this.setFile(event.dataTransfer.files && event.dataTransfer.files[0])
    },
    setFile (file) {
      this.errorMessage = ''
      this.rowCount = null
      if (!file) return

      if (!file.name.toLowerCase().endsWith('.csv')) {
        this.errorMessage = 'กรุณาเลือกไฟล์ .csv ก่อน'
        this.file = null
        return
      }

      if (file.size > MAX_FILE_SIZE) {
        this.errorMessage = 'ไฟล์ใหญ่เกิน 5 MB'
        this.file = null
        return
      }

      this.file = file
      this.countRows(file)
    },
    countRows (file) {
      const reader = new FileReader()
      reader.onload = () => {
        const rows = String(reader.result)
          .split(/\r\n|\r|\n/)
          .filter((line) => line.trim() !== '')
        this.rowCount = Math.max(0, rows.length - 1)
        if (this.rowCount > MAX_ROWS) {
          this.errorMessage = `ไฟล์มี ${this.rowCount.toLocaleString('en-US')} แถว เกินเพดาน ${this.maxRowsText} แถว กรุณาแบ่งไฟล์`
        }
      }
      reader.onerror = () => {
        this.rowCount = null
      }
      reader.readAsText(file)
    },
    close () {
      if (this.busy) return
      this.active = false
    },
    resetForm () {
      this.fromWarehouseId = null
      this.dueDate = ''
      this.dueDateError = ''
      this.file = null
      this.rowCount = null
      this.dragOver = false
      this.errorMessage = ''
      this.busy = false
    },
    async submit () {
      this.errorMessage = ''
      this.dueDateError = ''

      if (!this.fromWarehouseId) {
        this.errorMessage = 'กรุณาเลือกคลังต้นทางก่อน'
        return
      }

      if (!this.file) {
        this.errorMessage = 'กรุณาเลือกไฟล์ .csv ก่อน'
        return
      }

      if (this.rowCount !== null && this.rowCount > MAX_ROWS) {
        this.errorMessage = `ไฟล์มี ${this.rowCount.toLocaleString('en-US')} แถว เกินเพดาน ${this.maxRowsText} แถว กรุณาแบ่งไฟล์`
        return
      }

      // ค่า YYYY-MM-DD เทียบเป็น string ได้ตรงๆ; พิมพ์มือหรือค้างหน้าจอข้ามวันก็ยังโดนเช็ค
      if (this.dueDate && this.dueDate < this.$dayjs().format('YYYY-MM-DD')) {
        this.dueDateError = 'Due date ต้องไม่น้อยกว่าวันปัจจุบัน'
        return
      }

      const formData = new FormData()
      formData.append('file', this.file)
      formData.append('fromWarehouseId', this.fromWarehouseId)
      if (this.dueDate) formData.append('dueDate', this.dueDate)

      this.busy = true
      try {
        const { data } = await batchTransferProvider.create(formData)
        this.active = false
        this.$emit('uploaded', data)
      } catch (error) {
        this.errorMessage = this.readError(error)
      } finally {
        this.busy = false
      }
    },
    readError (error) {
      const message = error && error.message
      if (Array.isArray(message)) return message.join(' · ')
      return message || 'อัปโหลดไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง'
    }
  }
}
</script>

<style src="@/assets/css/batch-transfer.css"></style>
